import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';

import OutputText from '@ey/canvascoreservices/OutputText';

import {labels, currentResource} from '../../../../../../../../util/utils';
import {concurrencyErrorCode, inventedConcurrencyErrorCode} from '../../../../../../../../util/uiconstants';

import normalizeAddContact from '../normalizeAddContact';
import normalizeAddAllContacts from '../../ContactsToAdd/normalizeAddAllContacts';

import Modal from '../../../../../../../Common/Modal/Modal';
import LoadingIndicator from '../../../../../../../Common/LoadingIndicator';

import {addPotentialContact} from '../../../../../../../../actions/ContactDBAccessLog';
import {updateDisplayData} from '../../../../../../../../actions/Metrics/dashboardContextAction';
import {raiseJSValidationError} from '../../../../../../../../actions/erroractions';
import {addAllPotentialContacts} from '../../../../../../../../actions/ContactDBAccessLog';

import StyledContent from './StyledContent';
import MissingDomainsTable from './MissingDomainsTable';

const ConfirmationModal = ({
	missingDomains,
	contactsToAdd,
	projectId,
	entityId,
	entities,
	engagementMetadata,
	engagements,
	orgChart,
	isAddAll,
	message
}) => {
	const [open, setOpen] = useState(true);
	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useDispatch();

	useEffect(() => {
		setOpen(true);
	}, [missingDomains]);

	const displayCustomError = (error, users) => {
		setIsLoading(false);
		const errorCode = error?.response?.data?.errors?.Error[0]?.split('-')[0].trim() || '';
		if (errorCode === concurrencyErrorCode) {
			dispatch(
				raiseJSValidationError(
					inventedConcurrencyErrorCode +
						' - ' +
						currentResource.Errors[errorCode].replace('{0}', users?.name + ' ' + users?.lastName)
				)
			);
		}
	};

	const dispatchAddAllPotentialContacts = () => {
		const apiCalls = [];
		setIsLoading(true);
		let newEntities = [...entities];

		contactsToAdd
			.filter(({contacts}) => contacts?.length > 0)
			.forEach((entity) => {
				apiCalls.push(() =>
					dispatch(addAllPotentialContacts(projectId, entity.contacts, entity.entityId))
						.then(() => {
							newEntities = normalizeAddAllContacts(newEntities, entity.contacts, entity.entityId, engagementMetadata);
							dispatch(updateDisplayData(engagements, newEntities, orgChart));
						})
						.catch((error) => displayCustomError(error, entity.contacts))
				);
			});

		const sequentionallyDispatchApiArray = async () => {
			// This array of API calls needs to be executed sequentionally otherwise it fails (cos' CosmoDB)
			const results = [];
			for (const call of apiCalls) results.push(await Promise.allSettled([call()]));
			return results.flat();
		};

		sequentionallyDispatchApiArray()
			.then(() => {
				setIsLoading(false);
				setOpen(false);
			})
			.catch(() => {
				setIsLoading(false);
				setOpen(false);
			});
	};

	const dispatchAddPotentialContact = () => {
		const user = contactsToAdd[0];
		setIsLoading(true);
		dispatch(addPotentialContact(projectId, user.userId, user, entityId))
			.then(() => {
				const newEntities = normalizeAddContact(entities, user, entityId, engagementMetadata);
				dispatch(updateDisplayData(engagements, newEntities, orgChart));
				setIsLoading(false);
				setOpen(false);
			})
			.catch((error) => {
				displayCustomError(error, user);
				setOpen(false);
			});
	};

	const handlerOnConfirm = () => {
		if (!isLoading) {
			if (isAddAll) {
				dispatchAddAllPotentialContacts();
			} else {
				dispatchAddPotentialContact();
			}
		}
	};

	const renderConfirmationMessage = () => {
		const props = {size: 'L', weight: 'light'};
		return <OutputText {...props}>{message}</OutputText>;
	};

	const renderTableTitle = () => {
		const props = {size: 'L', weight: 'bold'};
		return <OutputText {...props}>{labels.placeholderEntities}</OutputText>;
	};

	const renderMissingDomains = () => {
		const props = {
			domains: missingDomains
		};

		return <MissingDomainsTable {...props} />;
	};

	const props = {
		show: open,
		modalsize: 'message',
		showButtons: true,
		title: labels.confirmDomain,
		closeTitle: labels.closeLabel,
		confirmBtnTitle: labels.continue,
		closeBtnTitle: labels.cancelLabel,
		isOkButtonDisabled: isLoading,
		isCancelButtonDisabled: isLoading,
		modalContainerClass: 'with-min-width',
		modalbodystyle: 'no-padding',
		onOkClick: () => {
			handlerOnConfirm();
		},
		onHide: () => {
			setOpen(false);
		}
	};

	return (
		<Modal modalonmodal {...props}>
			{<LoadingIndicator show={isLoading} />}
			<StyledContent className="customScrollbar">
				{renderConfirmationMessage()}
				<br />
				<br />
				{renderTableTitle()}
				<br />
				<br />
				{renderMissingDomains()}
				<br />
			</StyledContent>
		</Modal>
	);
};

ConfirmationModal.propTypes = {
	missingDomains: PropTypes.arrayOf.isRequired,
	contactsToAdd: PropTypes.arrayOf.isRequired,
	projectId: PropTypes.string.isRequired,
	entityId: PropTypes.string,
	entities: PropTypes.arrayOf.isRequired,
	engagementMetadata: PropTypes.arrayOf.isRequired,
	engagements: PropTypes.arrayOf.isRequired,
	orgChart: PropTypes.arrayOf.isRequired,
	isAddAll: PropTypes.bool.isRequired,
	message: PropTypes.string.isRequired
};

ConfirmationModal.defaultProps = {};

export default ConfirmationModal;
